import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Rocket, Bot, Timer } from "lucide-react";
import { useClicker, CoinBar, fmtCoins, blip } from "./ClickerLayout";

function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return now;
}
const left = (iso, now) => Math.max(0, Math.round((new Date(iso).getTime() - now) / 1000));

export default function ClickerBoost() {
  const { state, config, action, muted } = useClicker();
  const now = useNow();
  if (!config) return null;
  const autoOn = state.auto && left(state.auto.until, now) > 0;
  const use = async (id, b) => {
    const d = await action("/clicker/booster", { id });
    if (d) { blip(muted, 1200, 0.25, "sawtooth"); toast.success(d.gain ? `Super Click! +${fmtCoins(d.gain)}` : `${b.name} activated`); }
  };
  const status = (id) => {
    if (id === "super") { const s = state.super_ready_at && left(state.super_ready_at, now); return s ? `Cooldown ${s}s` : null; }
    const cur = id === "auto_earn" ? state.earn_booster : state.booster;
    if (cur && left(cur.until, now) > 0) return cur.id === id ? `Active · ${left(cur.until, now)}s` : "Another boost active";
    return null;
  };
  return (
    <div className="space-y-6" data-testid="clicker-boost">
      <CoinBar />
      <section className="space-y-2">
        <h2 className="font-black text-base flex items-center gap-2"><Rocket className="w-4 h-4 text-[#FF2BD6]" /> Boosters</h2>
        {Object.entries(config.boosters).map(([id, b]) => {
          const st = status(id);
          return (
            <div key={id} className="rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center gap-3" data-testid={`clicker-booster-${id}`}>
              <div className="flex-1">
                <div className="font-bold">{b.name}</div>
                <div className="text-[11px] text-white/50">
                  {id === "super" ? `Instantly earn ${b.taps} taps worth of coins` : id === "auto_earn" ? `${b.mult}x passive income for ${b.duration}s` : `${b.mult}x every tap for ${b.duration}s`}
                </div>
                {st && <div className="text-[11px] text-[#FFE300] mt-1 flex items-center gap-1"><Timer className="w-3 h-3" /> {st}</div>}
              </div>
              <button onClick={() => use(id, b)} disabled={!!st || state.coins < b.cost} data-testid={`clicker-booster-buy-${id}`}
                className="min-w-[80px] font-black text-xs px-3 py-2 rounded-full bg-[#FF2BD6] text-black disabled:bg-white/10 disabled:text-white/40">
                {fmtCoins(b.cost)}
              </button>
            </div>
          );
        })}
      </section>
      <section className="space-y-2">
        <h2 className="font-black text-base flex items-center gap-2"><Bot className="w-4 h-4 text-[#39FF14]" /> Auto clicker</h2>
        {autoOn ? (
          <div className="rounded-2xl bg-[#39FF14]/10 border border-[#39FF14]/30 p-4 space-y-3" data-testid="clicker-auto-panel">
            <div className="flex justify-between text-sm"><span className="font-bold">Running · {state.auto.tps} taps/sec</span><span>{left(state.auto.until, now)}s left</span></div>
            <input type="range" min={1} max={state.auto_max_tps} value={state.auto.tps} data-testid="clicker-auto-speed"
              onChange={(e) => action("/clicker/auto/speed", { tps: Number(e.target.value) })} className="w-full accent-[#39FF14]" />
            <div className="text-[10px] text-white/50">Max speed {state.auto_max_tps}/sec — raise it with the Auto Speed upgrade.</div>
          </div>
        ) : (
          Object.entries(config.auto_plans).map(([id, p]) => (
            <div key={id} className="rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center gap-3" data-testid={`clicker-auto-${id}`}>
              <div className="flex-1">
                <div className="font-bold">{p.name}</div>
                <div className="text-[11px] text-white/50">{p.tps}+ taps/sec for {Math.round(p.duration / 60)} min</div>
              </div>
              <button onClick={async () => { const d = await action("/clicker/auto", { id }); if (d) toast.success("Auto clicker started"); }}
                disabled={state.coins < p.cost} data-testid={`clicker-auto-buy-${id}`}
                className="min-w-[80px] font-black text-xs px-3 py-2 rounded-full bg-[#39FF14] text-black disabled:bg-white/10 disabled:text-white/40">
                {fmtCoins(p.cost)}
              </button>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
